import React, { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Scene from './InviteArt';
import OccasionMotif from './InviteMotifs';

/**
 * Renders an invitation design as either an animated "Card" (formal, opens from
 * an envelope) or a "Flyer" (casual, flat). Shared by the builder canvas (with
 * click-to-select text blocks) and the public invite page (tap-to-open).
 */

// Occasion artwork packs — emoji render everywhere and give each design its
// real vibe (a pool party looks like a pool party, a BBQ like a BBQ, …).
export const MOTIF_PACKS = {
  pool: ['🌴', '🦩', '🕶️', '🍍', '☀️', '🌊', '🍉', '🏖️'],
  tropical: ['🌴', '🌺', '🍹', '🥥', '🦜', '🌊', '🌸'],
  bbq: ['🔥', '🍔', '🌭', '🌽', '🍖', '🍗', '🥩', '🍺'],
  cookout: ['🍔', '🌭', '🍉', '🌽', '🔥', '🥤'],
  floral: ['🌿', '🌸', '🌷', '🤍', '💐', '🌾'],
  hearts: ['❤️', '💕', '💍', '🌹', '💖'],
  balloons: ['🎈', '🎉', '🎁', '🥳', '⭐', '🎊'],
  confetti: 'DOTS', geometric: 'DOTS',
  kids: ['🎈', '🧸', '🍭', '🎠', '⭐', '🎉'],
  stars: ['✡️', '⭐', '🕎', '✨', '🔷', '🌟'],
  mitzvah: ['✡️', '⭐', '🕎', '✨', '🔷'],
  spooky: ['🎃', '👻', '🦇', '🕸️', '🕷️', '🌙'],
  festive: ['🎄', '❄️', '🎁', '⭐', '🦌', '🔔'],
  winter: ['❄️', '⛄', '🌟', '🎿'],
  autumn: ['🍁', '🍂', '🦃', '🌾', '🎃'],
  patriotic: ['🎆', '🇺🇸', '⭐', '🎇', '🗽'],
  graduation: ['🎓', '📜', '⭐', '🎉', '🏅'],
  cocktail: ['🍸', '🥂', '🍾', '✨', '🍋'],
  brunch: ['🥂', '🍳', '🥐', '☕', '🍓'],
  sports: ['🏈', '🏀', '⚽', '🎉', '🍺'],
  babyshower: ['🍼', '👶', '🧸', '☁️', '⭐'],
  quinceanera: ['👑', '🌹', '✨', '💃'],
  housewarming: ['🏡', '🔑', '🪴', '🥂'],
  dinner: ['🍽️', '🍷', '🕯️', '🥂', '🍴'],
  home: ['🏡', '🔑', '🪴', '🥂', '🛋️'],
  artdeco: ['💎', '✨', '🥂', '🎩'],
  glam: ['✨', '👑', '💎', '🥂', '🎀', '⭐'],
  none: [],
};

// Where glyphs sit — edges & corners so central text stays legible.
const SPOTS = [
  { top: '5%', left: '7%', s: 22 }, { top: '6%', right: '7%', s: 18 },
  { bottom: '6%', left: '8%', s: 20 }, { bottom: '5%', right: '8%', s: 22 },
  { top: '38%', left: '3%', s: 15 }, { top: '44%', right: '3%', s: 16 },
  { top: '15%', left: '45%', s: 15 }, { bottom: '13%', left: '48%', s: 16 },
  { top: '24%', left: '14%', s: 13 }, { top: '26%', right: '14%', s: 13 },
];

const CASING = { upper: 'uppercase', lower: 'lowercase', title: 'capitalize' };

function Motif({ motif, accent, k = 1 }) {
  const pack = MOTIF_PACKS[motif];
  if (pack === 'DOTS' || motif === 'confetti' || motif === 'geometric') {
    const dots = Array.from({ length: 12 });
    const d = (motif === 'geometric' ? 9 : 6) * k;
    return (
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {dots.map((_, i) => (
          <span key={i} style={{
            position: 'absolute', top: `${(i * 37) % 92}%`, left: `${(i * 53) % 92}%`,
            width: d, height: d,
            borderRadius: motif === 'geometric' ? 2 : 9999,
            transform: motif === 'geometric' ? 'rotate(45deg)' : 'none',
            background: accent, opacity: 0.16,
          }} />
        ))}
      </div>
    );
  }
  const glyphs = Array.isArray(pack) ? pack : [];
  if (!glyphs.length) return null;
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {SPOTS.map((sp, i) => glyphs[i % glyphs.length] && (
        <span key={i} style={{ position: 'absolute', top: sp.top, bottom: sp.bottom, left: sp.left, right: sp.right, fontSize: Math.round(sp.s * k), opacity: 0.6, lineHeight: 1 }}>
          {glyphs[i % glyphs.length]}
        </span>
      ))}
    </div>
  );
}

function Block({ block, colors, font, editable, selected, onSelect, k = 1 }) {
  const isHeading = block.type === 'heading';
  const isSub = block.type === 'subheading';
  const style = {
    fontFamily: block.font || (isHeading || isSub ? font.heading : font.body),
    fontSize: (block.size || (isHeading ? 30 : isSub ? 15 : 13)) * k,
    color: block.color || (isSub ? colors.accent : colors.ink),
    textAlign: block.align || 'center',
    fontWeight: block.weight || (isHeading ? 700 : isSub ? 600 : 400),
    textTransform: CASING[block.casing] || 'none',
    letterSpacing: `${(block.letterSpacing ?? (isHeading ? -0.3 : 0)) * k}px`,
    lineHeight: block.lineHeight || 1.2,
    margin: `0 0 ${(isHeading ? 6 : 4) * k}px`,
    // Wrap between words; only break a single word if it can't fit the card.
    whiteSpace: 'pre-wrap', overflowWrap: 'break-word', hyphens: 'auto',
    cursor: editable ? 'pointer' : 'default',
    outline: selected ? '2px solid rgba(37,99,235,0.9)' : 'none',
    outlineOffset: 3, borderRadius: 3,
    transition: 'outline 0.12s',
  };
  // Optional per-block text effect (host-chosen in the Text tool).
  if (block.effect === 'shadow') style.textShadow = `${1.5 * k}px ${2 * k}px ${3 * k}px rgba(0,0,0,0.35)`;
  else if (block.effect === 'glow') style.textShadow = `0 0 ${8 * k}px ${hexA(block.color || colors.accent || '#fff', 0.8)}`;
  else if (block.effect === 'outline') {
    style.WebkitTextStroke = `${Math.max(0.6, 1 * k)}px ${block.color || colors.ink}`;
    style.WebkitTextFillColor = 'transparent';
    style.color = 'transparent';
  } else if (block.effect === 'lift') style.textShadow = `0 ${1 * k}px 0 rgba(255,255,255,0.6), 0 ${2 * k}px ${3 * k}px rgba(0,0,0,0.2)`;
  // Holographic / gold-foil title — the text itself becomes an animated metallic
  // gradient (CSS handles the sheen + reduced-motion opt-out).
  let fxClass = '';
  if (block.effect === 'holo') { fxClass = 'inv-holo-text'; style.color = undefined; }
  else if (block.effect === 'foil') { fxClass = 'inv-foil-text'; style.color = undefined; }
  // Curved / arched text — rendered as SVG textPath (HTML text can't follow a curve).
  if (block.curve) {
    return (
      <CurvedText block={block} style={style}
        editable={editable} selected={selected} onSelect={onSelect} />
    );
  }
  return (
    <div className={fxClass} style={style} onClick={editable ? (e) => { e.stopPropagation(); onSelect?.(block.id); } : undefined}
      data-block-id={block.id}>{block.text || ' '}</div>
  );
}

/**
 * A single text block bent along an arc. `block.curve` is a signed number
 * (roughly -100…100): positive arches the text upward (smile), negative
 * downward (frown), 0/undefined is off. The SVG scales to the card, so it stays
 * crisp at any preview size and matches the block's font, color, casing & weight.
 */
function CurvedText({ block, style, editable, selected, onSelect }) {
  const amt = Math.max(-100, Math.min(100, Number(block.curve) || 0));
  const text = block.text || ' ';
  const W = 300;
  const bend = (amt / 100) * 90;              // vertical travel of the arc's midpoint
  const H = 60 + Math.abs(bend);
  const fontSize = block.size || 30;
  const pad = fontSize * 0.9;
  const y0 = amt >= 0 ? H - pad : pad;        // endpoints sit low for a smile, high for a frown
  const cy = amt >= 0 ? y0 - Math.abs(bend) * 2 : y0 + Math.abs(bend) * 2;
  const pathId = `arc-${block.id}`;
  const d = `M ${pad} ${y0} Q ${W / 2} ${cy} ${W - pad} ${y0}`;
  const casing = CASING[block.casing];
  const shown = casing === 'uppercase' ? text.toUpperCase()
    : casing === 'lowercase' ? text.toLowerCase()
    : casing === 'capitalize' ? text.replace(/\b\w/g, (c) => c.toUpperCase()) : text;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ overflow: 'visible', display: 'block',
      margin: style.margin, cursor: editable ? 'pointer' : 'default',
      outline: selected ? '2px solid rgba(37,99,235,0.9)' : 'none', outlineOffset: 3, borderRadius: 3 }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect?.(block.id); } : undefined}
      data-block-id={block.id}>
      <defs><path id={pathId} d={d} fill="none" /></defs>
      <text textAnchor="middle" fill={style.color}
        style={{ fontFamily: style.fontFamily, fontWeight: style.fontWeight, textShadow: style.textShadow,
          WebkitTextStroke: style.WebkitTextStroke, letterSpacing: style.letterSpacing }}
        fontSize={fontSize}>
        <textPath href={`#${pathId}`} startOffset="50%">{shown}</textPath>
      </text>
    </svg>
  );
}

// Instagram-style attached-song pill: spinning disc, title/artist, equalizer, play/pause.
export function SongPlayer({ music, accent = '#2563EB', autoPlay = false, scale = 1 }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const url = typeof music === 'string' ? music : music?.url;
  const title = (music && music.title) || 'Untitled song';
  const artist = (music && music.artist) || '';
  useEffect(() => {
    if (!autoPlay || !url) return;
    const a = audioRef.current;
    if (a) a.play().then(() => setPlaying(true)).catch(() => {});
  }, [autoPlay, url]);
  const toggle = (e) => {
    e?.stopPropagation();
    const a = audioRef.current;
    if (!a) { setPlaying((p) => !p); return; }
    if (a.paused) a.play().then(() => setPlaying(true)).catch(() => {});
    else { a.pause(); setPlaying(false); }
  };
  return (
    <div className="flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-md pl-1.5 pr-3 py-1.5 shadow-lift" style={{ maxWidth: 210 * scale }}>
      <button type="button" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'} className="relative w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden">
        <span className={`absolute inset-0 rounded-full ${playing ? 'spin-disc' : ''}`}
          style={{ background: `radial-gradient(circle at center, rgba(255,255,255,0.9) 0 14%, ${accent} 15% 40%, #17171a 42%)` }} />
        <span className="relative text-white text-[11px] leading-none" style={{ marginLeft: playing ? 0 : 1 }}>{playing ? '❚❚' : '▶'}</span>
      </button>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold text-white truncate leading-tight">{title}</div>
        {artist && <div className="text-[10px] text-white/70 truncate leading-tight">{artist}</div>}
      </div>
      <div className="flex items-end gap-[2px] h-3.5 shrink-0" aria-hidden>
        {[0, 1, 2, 3].map((i) => <span key={i} className={`w-[2px] h-full bg-white rounded-full ${playing ? 'eq-bar' : ''}`} style={{ animationDelay: `${i * 0.13}s`, transform: playing ? undefined : 'scaleY(0.4)' }} />)}
      </div>
      {url && <audio ref={audioRef} src={url} loop preload="none" />}
    </div>
  );
}

// CSS filter presets applied to invitation photos (host-chosen in the Media tool).
const PHOTO_FILTERS = {
  none: 'none',
  bw: 'grayscale(1) contrast(1.05)',
  warm: 'sepia(0.35) saturate(1.25) contrast(1.02)',
  cool: 'saturate(1.1) hue-rotate(-12deg) brightness(1.03)',
  vintage: 'sepia(0.5) contrast(0.92) saturate(0.85) brightness(1.05)',
  mono: 'grayscale(1) brightness(1.08) contrast(1.1)',
  bright: 'brightness(1.12) saturate(1.2)',
};

// Photo carousel (Ken Burns) or background video, shown at the top of the card.
function MediaHero({ design, rich, heightPct = '42%' }) {
  const photos = (design.photos && design.photos.length) ? design.photos : (design.photo ? [design.photo] : []);
  const [idx, setIdx] = useState(0);
  const filter = PHOTO_FILTERS[design.photoFilter] || 'none';
  useEffect(() => {
    if (!rich || photos.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % photos.length), 3200);
    return () => clearInterval(t);
  }, [rich, photos.length]);
  if (design.video) {
    return (
      <div className="w-full overflow-hidden shrink-0" style={{ height: heightPct }}>
        <video src={design.video} muted loop autoPlay playsInline className="w-full h-full object-cover" style={{ filter }} />
      </div>
    );
  }
  if (!photos.length) return null;
  return (
    <div className="relative w-full overflow-hidden shrink-0" style={{ height: heightPct }}>
      {photos.map((p, i) => (
        <img key={i} src={p} alt="" style={{ filter }} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0'} ${rich ? 'kenburns' : ''}`} />
      ))}
    </div>
  );
}

// Confetti / petals / sparkles that rain down once when the card opens.
const REVEAL_GLYPH = { confetti: ['🎉', '🎊', '✨'], petals: ['🌸', '🌷', '🌿'], reveal: ['🌸', '🌷', '🌿'], bloom: ['🌸', '💮', '🌺'], sparkle: ['✨', '⭐', '💫'], hearts: ['❤️', '💖', '✨'] };
function RevealBurst({ kind, accent }) {
  const glyphs = REVEAL_GLYPH[kind];
  if (!glyphs) return null;
  const bits = Array.from({ length: 16 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-20" aria-hidden>
      {bits.map((_, i) => (
        <span key={i} className="inv-fall absolute" style={{
          left: `${(i * 61) % 96}%`, top: '-8%', fontSize: 10 + (i % 4) * 4,
          animationDuration: `${2 + (i % 5) * 0.4}s`, animationDelay: `${(i % 6) * 0.15}s`,
        }}>{glyphs[i % glyphs.length]}</span>
      ))}
    </div>
  );
}

// ═══════════ Creative card formats (music card, vinyl, boarding pass, polaroid, cassette) ═══════════
export const CREATIVE_FORMATS = ['nowplaying', 'vinyl', 'boarding', 'polaroid', 'cassette'];
const txt = (design, id) => { const b = (design.blocks || []).find((x) => x.id === id); return (b && b.text) || ''; };
const firstPhoto = (design) => (design.photos && design.photos[0]) || design.photo || null;
const initials = (s) => ((s || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase()) || '♪';
function shade(hex, pct) {
  const h = (hex || '').replace('#', ''); if (h.length !== 6) return hex;
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + (pct / 100) * 255)));
  const r = f(parseInt(h.slice(0, 2), 16)), g = f(parseInt(h.slice(2, 4), 16)), b = f(parseInt(h.slice(4, 6), 16));
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}
const SCRIPT = "'Caveat','Snell Roundhand','Segoe Script',cursive";

function NowPlayingArt({ design, scale, colors, font, rich, playing }) {
  const accent = colors.accent || '#2563EB';
  const photo = firstPhoto(design);
  const title = txt(design, 'title') || 'Your Event';
  const sub = [txt(design, 'date'), txt(design, 'location')].filter(Boolean).join(' · ') || "You're invited";
  const music = design.music && (typeof design.music === 'object' ? design.music : { url: design.music, title: 'Attached song' });
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  useEffect(() => { if (rich && playing && music?.url) audioRef.current?.play().then(() => setIsPlaying(true)).catch(() => {}); }, [rich, playing, music?.url]);
  const toggle = (e) => { e?.stopPropagation(); const a = audioRef.current; if (!a || !music?.url) { setIsPlaying((v) => !v); return; } if (a.paused) a.play().then(() => setIsPlaying(true)).catch(() => {}); else { a.pause(); setIsPlaying(false); } };
  const p = (v) => Math.round(v * scale);
  return (
    <div className="w-full h-full flex flex-col items-center" style={{ background: `linear-gradient(165deg, ${accent}, #141019 80%)`, padding: `${p(0.08)}px ${p(0.08)}px`, color: '#fff', fontFamily: font.body }}>
      <div className="font-semibold" style={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.25em', fontSize: p(0.028), marginBottom: p(0.03) }}>NOW PLAYING</div>
      <div className="relative rounded-2xl overflow-hidden" style={{ width: '76%', aspectRatio: '1/1', marginBottom: p(0.05), boxShadow: '0 16px 40px rgba(0,0,0,0.45)', background: photo ? undefined : `linear-gradient(135deg, ${colors.bg || '#fff'}, ${accent})` }}>
        {photo ? <img src={photo} alt="" className={`w-full h-full object-cover ${rich && isPlaying ? 'kenburns' : ''}`} /> : <div className="w-full h-full flex items-center justify-center" style={{ fontSize: p(0.22) }}>🎵</div>}
      </div>
      <div className="w-full text-center" style={{ marginBottom: p(0.035) }}>
        <div className="font-bold leading-tight truncate" style={{ fontFamily: font.heading, fontSize: p(0.075) }}>{title}</div>
        <div className="truncate" style={{ color: 'rgba(255,255,255,0.65)', fontSize: p(0.04), marginTop: p(0.012) }}>{sub}</div>
      </div>
      <div className="w-full" style={{ marginBottom: p(0.035) }}>
        <div className="w-full rounded-full" style={{ height: Math.max(2, p(0.008)), background: 'rgba(255,255,255,0.25)' }}>
          <div className="rounded-full bg-white" style={{ width: '38%', height: '100%' }} />
        </div>
        <div className="flex justify-between" style={{ color: 'rgba(255,255,255,0.55)', fontSize: p(0.026), marginTop: p(0.012) }}>
          <span>1:12</span><span className="truncate" style={{ maxWidth: '60%' }}>{music?.title ? `♪ ${music.title}` : '3:04'}</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-5" style={{ fontSize: p(0.045) }}>
        <span style={{ opacity: 0.7 }}>⏮</span>
        <button type="button" onClick={toggle} className="flex items-center justify-center rounded-full bg-white text-black" style={{ width: p(0.15), height: p(0.15), fontSize: p(0.06) }} aria-label={isPlaying ? 'Pause' : 'Play'}>{isPlaying ? '❚❚' : '▶'}</button>
        <span style={{ opacity: 0.7 }}>⏭</span>
      </div>
      {music?.url && <audio ref={audioRef} src={music.url} loop preload="none" />}
    </div>
  );
}

function VinylArt({ design, scale, colors, font, rich }) {
  const accent = colors.accent || '#2563EB';
  const ink = colors.ink || '#232323';
  const bg = colors.bg || '#f1ece3';
  const photo = firstPhoto(design);
  const title = txt(design, 'title') || 'Your Event';
  const sub = [txt(design, 'date'), txt(design, 'location')].filter(Boolean).join(' · ');
  const p = (v) => Math.round(v * scale);
  const ring = Math.max(1, p(0.004));
  return (
    <div className="w-full h-full flex flex-col items-center justify-center" style={{ background: `radial-gradient(120% 90% at 50% 0%, ${shade(bg, 6)}, ${shade(bg, -10)})`, padding: p(0.08), fontFamily: font.body }}>
      <div className="font-semibold" style={{ color: accent, letterSpacing: '0.3em', fontSize: p(0.03), marginBottom: p(0.035) }}>SIDE A</div>
      <div className="relative rounded-full" style={{ width: '72%', aspectRatio: '1/1', marginBottom: p(0.05), background: `repeating-radial-gradient(circle at center, #16161a 0 ${ring}px, #212127 ${ring}px ${p(0.013)}px)`, boxShadow: '0 12px 34px rgba(0,0,0,0.4)' }}>
        <div className={`absolute inset-0 rounded-full ${rich ? 'spin-disc' : ''}`} style={{ animationDuration: '6s' }}>
          <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(120deg, transparent 42%, rgba(255,255,255,0.12) 50%, transparent 58%)' }} />
          <div className="absolute rounded-full overflow-hidden flex items-center justify-center text-center" style={{ inset: '32%', background: photo ? undefined : accent, color: '#fff' }}>
            {photo ? <img src={photo} className="w-full h-full object-cover" alt="" /> : <span style={{ fontFamily: font.heading, fontWeight: 700, fontSize: p(0.05) }}>{initials(title)}</span>}
          </div>
          <div className="absolute rounded-full" style={{ inset: '47.5%', background: shade(bg, 2) }} />
        </div>
      </div>
      <div className="text-center w-full">
        <div className="font-bold truncate" style={{ fontFamily: font.heading, color: ink, fontSize: p(0.07) }}>{title}</div>
        {sub && <div className="truncate" style={{ color: accent, fontSize: p(0.038), marginTop: p(0.01) }}>{sub}</div>}
      </div>
    </div>
  );
}

function BoardingArt({ design, scale, colors, font }) {
  const accent = colors.accent || '#2563EB';
  const ink = colors.ink || '#1a1a1a';
  const bg = colors.bg || '#eef2f7';
  const title = txt(design, 'title') || 'Your Event';
  const date = txt(design, 'date'); const location = txt(design, 'location') || 'The Venue';
  const p = (v) => Math.round(v * scale);
  const muted = '#9aa3af';
  const cell = (lbl, val) => (
    <div className="min-w-0"><div style={{ color: muted, fontSize: p(0.022), letterSpacing: '0.1em' }}>{lbl}</div><div className="truncate" style={{ color: ink, fontSize: p(0.032), fontWeight: 600 }}>{val || '—'}</div></div>
  );
  return (
    <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(160deg, ${bg}, ${shade(bg, -7)})`, padding: p(0.05), fontFamily: font.body }}>
      <div className="w-full bg-white rounded-xl overflow-hidden" style={{ transform: 'rotate(-2deg)', boxShadow: '0 14px 34px rgba(0,0,0,0.28)' }}>
        <div className="flex items-center justify-between" style={{ background: accent, color: '#fff', padding: `${p(0.028)}px ${p(0.045)}px` }}>
          <span className="font-bold" style={{ fontSize: p(0.034), letterSpacing: '0.05em' }}>BOARDING PASS</span><span style={{ fontSize: p(0.045) }}>✈</span>
        </div>
        <div style={{ padding: p(0.045) }}>
          <div style={{ color: muted, fontSize: p(0.022), letterSpacing: '0.12em' }}>YOU'RE INVITED TO</div>
          <div className="font-bold truncate" style={{ fontFamily: font.heading, color: ink, fontSize: p(0.058), marginBottom: p(0.03) }}>{title}</div>
          <div className="flex items-center justify-between" style={{ marginBottom: p(0.035) }}>
            <div className="text-center"><div className="font-bold" style={{ color: ink, fontSize: p(0.048) }}>YOU</div><div style={{ color: muted, fontSize: p(0.022) }}>GUEST</div></div>
            <div className="flex-1 flex items-center" style={{ color: accent, padding: `0 ${p(0.02)}px` }}>
              <div className="flex-1 border-t border-dashed" style={{ borderColor: accent }} /><span style={{ fontSize: p(0.038) }}>✈</span><div className="flex-1 border-t border-dashed" style={{ borderColor: accent }} />
            </div>
            <div className="text-center" style={{ maxWidth: '38%' }}><div className="font-bold truncate" style={{ color: ink, fontSize: p(0.048) }}>{initials(location)}</div><div className="truncate" style={{ color: muted, fontSize: p(0.022) }}>DEST</div></div>
          </div>
          <div className="grid grid-cols-3" style={{ gap: p(0.02) }}>{cell('DATE', date)}{cell('LOCATION', location)}{cell('SEAT', 'VIP')}</div>
        </div>
        <div style={{ borderTop: `2px dashed ${shade(bg, -16)}` }} />
        <div className="flex items-center justify-between" style={{ padding: `${p(0.028)}px ${p(0.045)}px` }}>
          <div className="flex items-end" style={{ height: p(0.055), gap: 1 }}>
            {Array.from({ length: 22 }).map((_, i) => <span key={i} style={{ width: (i % 3 ? 1 : 2), height: '100%', background: i % 4 ? ink : 'rgba(0,0,0,0.55)' }} />)}
          </div>
          <span style={{ color: muted, fontSize: p(0.024) }}>BKR·{initials(title)}01</span>
        </div>
      </div>
    </div>
  );
}

function PolaroidArt({ design, scale, colors, font, rich }) {
  const accent = colors.accent || '#2563EB';
  const bg = colors.bg || '#e6e2da';
  const photo = firstPhoto(design);
  const title = txt(design, 'title') || 'Your Event';
  const sub = [txt(design, 'date'), txt(design, 'location')].filter(Boolean).join(' · ');
  const p = (v) => Math.round(v * scale);
  return (
    <div className="w-full h-full flex items-center justify-center relative" style={{ background: `radial-gradient(120% 100% at 50% 0%, ${shade(bg, 7)}, ${shade(bg, -12)})`, padding: p(0.07) }}>
      <div className="absolute bg-white" style={{ width: '60%', aspectRatio: '0.82/1', transform: 'rotate(7deg) translateY(6%)', borderRadius: 3, opacity: 0.5, boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }} />
      <div className="relative bg-white" style={{ width: '72%', transform: 'rotate(-4deg)', borderRadius: 4, padding: `${p(0.03)}px ${p(0.03)}px ${p(0.018)}px`, boxShadow: '0 16px 36px rgba(0,0,0,0.32)' }}>
        <div className="absolute left-1/2" style={{ transform: 'translateX(-50%) rotate(-3deg)', top: -p(0.018), width: '34%', height: p(0.045), background: 'rgba(214,203,160,0.6)' }} />
        <div className="overflow-hidden" style={{ width: '100%', aspectRatio: '1/1', borderRadius: 2, background: photo ? undefined : `linear-gradient(135deg, ${shade(bg, 12)}, ${accent})` }}>
          {photo ? <img src={photo} className={`w-full h-full object-cover ${rich ? 'kenburns' : ''}`} alt="" /> : <div className="w-full h-full flex items-center justify-center" style={{ fontSize: p(0.16) }}>📸</div>}
        </div>
        <div className="text-center" style={{ padding: `${p(0.028)}px 0 ${p(0.012)}px` }}>
          <div className="truncate" style={{ fontFamily: SCRIPT, color: '#333', fontSize: p(0.078), lineHeight: 1 }}>{title}</div>
          {sub && <div className="truncate" style={{ fontFamily: SCRIPT, color: '#8a8a8a', fontSize: p(0.046) }}>{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function CassetteArt({ design, scale, colors, font, rich }) {
  const accent = colors.accent || '#2563EB';
  const bg = colors.bg || '#1d1d22';
  const body = shade(accent, -12);
  const title = txt(design, 'title') || 'Your Event';
  const sub = [txt(design, 'date'), txt(design, 'location')].filter(Boolean).join(' · ');
  const p = (v) => Math.round(v * scale);
  const Reel = () => (
    <div className={`relative rounded-full flex items-center justify-center ${rich ? 'spin-disc' : ''}`} style={{ width: p(0.14), height: p(0.14), background: '#2a2a2e', animationDuration: '3s' }}>
      <div className="absolute rounded-full" style={{ inset: p(0.028), border: '2px solid #565660' }} />
      {Array.from({ length: 6 }).map((_, i) => <div key={i} className="absolute" style={{ width: 2, height: p(0.052), background: '#565660', transform: `rotate(${i * 60}deg)` }} />)}
      <div className="rounded-full" style={{ width: p(0.03), height: p(0.03), background: '#7a7a84' }} />
    </div>
  );
  return (
    <div className="w-full h-full flex flex-col items-center justify-center" style={{ background: `linear-gradient(160deg, ${shade(bg, 5)}, ${shade(bg, -9)})`, padding: p(0.06), fontFamily: font.body }}>
      <div className="w-full rounded-lg" style={{ background: body, padding: p(0.035), boxShadow: '0 14px 34px rgba(0,0,0,0.45)' }}>
        <div className="rounded-sm" style={{ background: '#f6f2e8', padding: p(0.03), marginBottom: p(0.03) }}>
          <div className="flex items-center justify-between" style={{ borderBottom: `1px solid ${accent}`, paddingBottom: p(0.012), marginBottom: p(0.015) }}>
            <span style={{ color: accent, fontWeight: 700, fontSize: p(0.026), letterSpacing: '0.1em' }}>MIXTAPE</span><span style={{ color: '#a0a0a0', fontSize: p(0.024) }}>90 min</span>
          </div>
          <div className="truncate" style={{ fontFamily: SCRIPT, color: '#333', fontSize: p(0.062), lineHeight: 1.05 }}>{title}</div>
          {sub && <div className="truncate" style={{ fontFamily: SCRIPT, color: '#8a8a8a', fontSize: p(0.04) }}>{sub}</div>}
        </div>
        <div className="rounded-md flex items-center justify-between" style={{ background: '#141416', padding: `${p(0.03)}px ${p(0.05)}px` }}>
          <Reel />
          <div className="flex-1 rounded-sm" style={{ height: p(0.05), margin: `0 ${p(0.03)}px`, background: 'linear-gradient(#3a2f22,#241c14)' }} />
          <Reel />
        </div>
        <div className="flex justify-around" style={{ marginTop: p(0.02) }}>
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="rounded-full" style={{ width: p(0.02), height: p(0.02), background: 'rgba(0,0,0,0.4)' }} />)}
        </div>
      </div>
    </div>
  );
}

export function CreativeFace({ design, scale = 300, format, rich = false, playing = false }) {
  const colors = design.colors || {};
  const font = design.font || { heading: "'Outfit', sans-serif", body: "'Manrope', sans-serif" };
  const props = { design, scale, colors, font, rich, playing };
  const art =
    format === 'nowplaying' ? <NowPlayingArt {...props} />
    : format === 'vinyl' ? <VinylArt {...props} />
    : format === 'boarding' ? <BoardingArt {...props} />
    : format === 'polaroid' ? <PolaroidArt {...props} />
    : format === 'cassette' ? <CassetteArt {...props} />
    : null;
  return (
    <div className="relative overflow-hidden rounded-lg shadow-lift" style={{ width: scale, aspectRatio: '3 / 4' }} data-testid="invite-card-face" data-format={format}>
      {art}
    </div>
  );
}

// ═══════════════ Decorative primitives — the premium look ═══════════════
// Templates opt into these via design.decor = { frame, pattern, texture }.
const hexA = (hex, a) => {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return `rgba(0,0,0,${a})`;
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${a})`;
};

// Subtle film grain — a big premium lift for almost no cost.
function Grain({ opacity = 0.05 }) {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none z-[1]" style={{ opacity, mixBlendMode: 'multiply' }} aria-hidden>
      <filter id="inv-grain"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" /></filter>
      <rect width="100%" height="100%" filter="url(#inv-grain)" />
    </svg>
  );
}

// Is the card stock dark? Drives which foil variant reads correctly — a metallic
// glare on dark stock, a softer paper sheen on light.
function isDarkCard(hex) {
  const h = String(hex || '#fff').replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(f, 16);
  if (Number.isNaN(n)) return false;
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 128;
}

// Background patterns — subtle, tasteful, tinted with the accent.
function DecorPattern({ kind, color, color2, scale = 300 }) {
  const c = color || '#888', c2 = color2 || c;
  const s = Math.max(9, Math.round(scale * 0.058));
  if (kind === 'checker')
    return <div className="absolute inset-0 pointer-events-none" aria-hidden style={{ backgroundImage: `repeating-conic-gradient(${hexA(c, 0.13)} 0% 25%, transparent 0% 50%)`, backgroundSize: `${s}px ${s}px` }} />;
  if (kind === 'dot')
    return <div className="absolute inset-0 pointer-events-none" aria-hidden style={{ backgroundImage: `radial-gradient(${hexA(c, 0.18)} ${Math.max(1, scale * 0.006)}px, transparent ${Math.max(1.5, scale * 0.008)}px)`, backgroundSize: `${s}px ${s}px` }} />;
  if (kind === 'grid')
    return <div className="absolute inset-0 pointer-events-none" aria-hidden style={{ backgroundImage: `linear-gradient(${hexA(c, 0.1)} 1px, transparent 1px), linear-gradient(90deg, ${hexA(c, 0.1)} 1px, transparent 1px)`, backgroundSize: `${s}px ${s}px` }} />;
  if (kind === 'stripe')
    return <div className="absolute inset-0 pointer-events-none" aria-hidden style={{ backgroundImage: `repeating-linear-gradient(45deg, ${hexA(c, 0.09)} 0 ${Math.round(scale * 0.01)}px, transparent ${Math.round(scale * 0.01)}px ${Math.round(scale * 0.05)}px)` }} />;
  if (kind === 'vstripe')
    return <div className="absolute inset-0 pointer-events-none" aria-hidden style={{ backgroundImage: `repeating-linear-gradient(90deg, ${hexA(c, 0.09)} 0 ${Math.round(s / 2)}px, transparent ${Math.round(s / 2)}px ${s}px)` }} />;
  if (kind === 'wave') {
    const rows = Array.from({ length: 7 });
    return (
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 133" preserveAspectRatio="none" aria-hidden>
        {rows.map((_, i) => (
          <path key={i} d={`M0 ${8 + i * 18} Q 12.5 ${1 + i * 18} 25 ${8 + i * 18} T 50 ${8 + i * 18} T 75 ${8 + i * 18} T 100 ${8 + i * 18}`} fill="none" stroke={hexA(c, 0.16)} strokeWidth="1.4" />
        ))}
      </svg>
    );
  }
  if (kind === 'sunburst') {
    const rays = Array.from({ length: 24 });
    return (
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden>
        {rays.map((_, i) => {
          const a = (i / rays.length) * Math.PI * 2;
          return <line key={i} x1="50" y1="50" x2={50 + Math.cos(a) * 90} y2={50 + Math.sin(a) * 90} stroke={hexA(c, i % 2 ? 0.05 : 0.11)} strokeWidth="4" />;
        })}
      </svg>
    );
  }
  if (kind === 'terrazzo') {
    const bits = Array.from({ length: 30 });
    const cols = [hexA(c, 0.5), hexA(c2, 0.5), hexA(c, 0.28)];
    return (
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 133" preserveAspectRatio="none" aria-hidden>
        {bits.map((_, i) => (
          <circle key={i} cx={(i * 37) % 100} cy={(i * 53) % 133} r={0.8 + (i % 3) * 0.7} fill={cols[i % 3]} />
        ))}
      </svg>
    );
  }
  if (kind === 'bows') {
    // Scattered ribbon bows — the 2026 coquette / Rococo signature.
    const pts = Array.from({ length: 15 });
    const Bow = ({ x, y, r }) => (
      <g transform={`translate(${x} ${y}) rotate(${r})`} opacity="0.55">
        <path d="M0 0 L-4 -2.6 Q-5.4 0 -4 2.6 Z" fill={hexA(c, 0.5)} />
        <path d="M0 0 L4 -2.6 Q5.4 0 4 2.6 Z" fill={hexA(c, 0.5)} />
        <path d="M0 0 L-1.4 4.6 M0 0 L1.4 4.6" stroke={hexA(c, 0.5)} strokeWidth="0.7" strokeLinecap="round" />
        <circle cx="0" cy="0" r="1" fill={hexA(c, 0.72)} />
      </g>
    );
    return (
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 133" preserveAspectRatio="xMidYMid slice" aria-hidden>
        {pts.map((_, i) => <Bow key={i} x={(i * 31 + 8) % 100} y={(i * 41 + 12) % 133} r={(i % 3 - 1) * 12} />)}
      </svg>
    );
  }
  if (kind === 'lace') {
    // Eyelet lace trim along the top and bottom edges — heirloom / whitework feel.
    const cols = Array.from({ length: 16 });
    return (
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 160 213" preserveAspectRatio="none" aria-hidden>
        {cols.map((_, i) => (
          <g key={i} stroke={hexA(c, 0.34)} strokeWidth="0.7" fill="none">
            <path d={`M${i * 10} 3 a5 5 0 0 0 10 0`} />
            <circle cx={i * 10 + 5} cy="9" r="1.4" />
            <path d={`M${i * 10} 210 a5 5 0 0 1 10 0`} />
            <circle cx={i * 10 + 5} cy="204" r="1.4" />
          </g>
        ))}
      </svg>
    );
  }
  return null;
}

// Decorative frames / borders.
function DecorFrame({ kind, color, scale = 300 }) {
  const c = color || '#888';
  const inset = Math.round(scale * 0.05);
  const corner = Math.round(scale * 0.11);
  if (kind === 'hairline')
    return <div className="absolute pointer-events-none z-[2]" aria-hidden style={{ inset, border: `1px solid ${hexA(c, 0.65)}`, borderRadius: 3 }} />;
  if (kind === 'double')
    return (<><div className="absolute pointer-events-none z-[2]" aria-hidden style={{ inset, border: `2px solid ${c}` }} /><div className="absolute pointer-events-none z-[2]" aria-hidden style={{ inset: inset + Math.round(scale * 0.014), border: `1px solid ${hexA(c, 0.55)}` }} /></>);
  if (kind === 'deco') {
    const B = ({ style }) => <div className="absolute pointer-events-none z-[2]" aria-hidden style={{ width: corner, height: corner, ...style }} />;
    const line = `2px solid ${c}`;
    return (<>
      <div className="absolute pointer-events-none z-[2]" aria-hidden style={{ inset: inset + Math.round(scale * 0.02), border: `1px solid ${hexA(c, 0.4)}` }} />
      <B style={{ top: inset, left: inset, borderTop: line, borderLeft: line }} />
      <B style={{ top: inset, right: inset, borderTop: line, borderRight: line }} />
      <B style={{ bottom: inset, left: inset, borderBottom: line, borderLeft: line }} />
      <B style={{ bottom: inset, right: inset, borderBottom: line, borderRight: line }} />
    </>);
  }
  if (kind === 'arch') {
    const r = Math.round(scale * 0.44);
    return (
      <div className="absolute pointer-events-none z-[2]" aria-hidden style={{ inset, borderRadius: `${r}px ${r}px ${Math.round(scale * 0.03)}px ${Math.round(scale * 0.03)}px`, border: `1.5px solid ${hexA(c, 0.7)}` }} />
    );
  }
  if (kind === 'botanical') {
    const Sprig = ({ style }) => (
      <svg width={corner * 1.4} height={corner * 1.4} viewBox="0 0 40 40" className="absolute pointer-events-none z-[2]" style={style} aria-hidden>
        <path d="M6 34 Q 18 26 20 8" fill="none" stroke={hexA(c, 0.8)} strokeWidth="1.3" strokeLinecap="round" />
        {[10, 15, 20, 25].map((t, i) => (
          <g key={i} transform={`translate(${13 - i * 1.5} ${30 - i * 5})`}>
            <ellipse cx="-4" cy="0" rx="4" ry="1.7" fill={hexA(c, 0.55)} transform="rotate(-30)" />
            <ellipse cx="4" cy="0" rx="4" ry="1.7" fill={hexA(c, 0.55)} transform="rotate(30)" />
          </g>
        ))}
      </svg>
    );
    return (<>
      <Sprig style={{ top: inset - 4, left: inset - 4 }} />
      <Sprig style={{ top: inset - 4, right: inset - 4, transform: 'scaleX(-1)' }} />
      <Sprig style={{ bottom: inset - 4, left: inset - 4, transform: 'scaleY(-1)' }} />
      <Sprig style={{ bottom: inset - 4, right: inset - 4, transform: 'scale(-1)' }} />
    </>);
  }
  return null;
}

// Compose the background layer (solid / gradient / radial / duotone) as CSS.
function bgLayer(design, colors) {
  const bg = design.background || {};
  const base = bg.color || colors.bg || '#fff';
  if (design.backdrop) return { backgroundColor: undefined, backgroundImage: `url(${design.backdrop})`, backgroundSize: 'cover', backgroundPosition: 'center' };
  if (bg.type === 'gradient') return { backgroundImage: `linear-gradient(${bg.angle || 150}deg, ${bg.from || base}, ${bg.to || colors.accent})` };
  if (bg.type === 'radial') return { backgroundImage: `radial-gradient(120% 120% at ${bg.at || '50% 0%'}, ${bg.from || base}, ${bg.to || colors.accent})` };
  if (bg.type === 'mesh') return { backgroundColor: base, backgroundImage: `radial-gradient(60% 60% at 15% 12%, ${hexA(bg.from || colors.accent, 0.55)}, transparent 60%), radial-gradient(55% 55% at 88% 20%, ${hexA(bg.to || colors.accent2 || colors.accent, 0.5)}, transparent 55%), radial-gradient(70% 60% at 50% 100%, ${hexA(colors.accent2 || colors.accent, 0.35)}, transparent 60%)` };
  return { backgroundColor: base };
}

/**
 * Ambient drift — 2–3 soft, blurred color blobs that slowly roam behind the
 * card, so even a plain background feels alive (CSS-only, GPU transform). Opt in
 * with design.ambient === 'drift' (or any truthy occasion default).
 */
function AmbientLayer({ colors = {}, tone }) {
  const a = colors.accent || '#8ab4ff';
  const b = colors.accent2 || colors.accent || '#c9a8ff';
  const c = colors.liner || colors.accent || '#ffd1e8';
  const blobs = [
    { bg: a, w: '62%', h: '58%', left: '-8%', top: '-6%' },
    { bg: b, w: '58%', h: '54%', right: '-10%', top: '18%' },
    { bg: c, w: '66%', h: '60%', left: '10%', bottom: '-14%' },
  ];
  return (
    <div className="inv-amb" aria-hidden>
      {blobs.map((s, i) => (
        <span key={i} className="inv-amb__blob"
          style={{ background: s.bg, width: s.w, height: s.h, left: s.left, right: s.right, top: s.top, bottom: s.bottom,
            opacity: tone === 'dark' ? 0.42 : 0.5 }} />
      ))}
    </div>
  );
}

// Themed particle presets — each occasion family gets a fitting drift.
const PARTICLE_PRESETS = {
  petals:   { glyphs: ['🌸', '🌷', '❀'], count: 10, mode: 'fall', size: [12, 22], dur: [7, 12] },
  confetti: { glyphs: ['▪', '●', '▲', '★'], count: 16, mode: 'fall', size: [6, 12], dur: [4, 8], colored: true },
  snow:     { glyphs: ['❄', '•', '❅'], count: 14, mode: 'fall', size: [8, 16], dur: [8, 14] },
  bokeh:    { glyphs: ['●'], count: 12, mode: 'bokeh', size: [6, 18], dur: [4, 9], colored: true },
  hearts:   { glyphs: ['♥', '❤'], count: 9, mode: 'rise', size: [12, 22], dur: [6, 11] },
  balloons: { glyphs: ['🎈'], count: 7, mode: 'rise', size: [16, 28], dur: [8, 13] },
  stars:    { glyphs: ['✦', '✧', '⋆'], count: 12, mode: 'bokeh', size: [8, 16], dur: [3, 7] },
  leaves:   { glyphs: ['🍂', '🍁'], count: 9, mode: 'fall', size: [12, 22], dur: [7, 12] },
  sparkle:  { glyphs: ['✨', '·'], count: 11, mode: 'bokeh', size: [8, 18], dur: [3, 6] },
};

/**
 * Floating particles (petals, confetti, snow, bokeh, balloons…). Positions and
 * timings are derived deterministically from the index so the layer is stable
 * across re-renders (no Math.random, which also keeps SSR/preview consistent).
 */
function ParticleLayer({ kind, colors = {}, scale = 300 }) {
  const preset = PARTICLE_PRESETS[kind];
  if (!preset) return null;
  const k = Math.min(1.3, Math.max(0.5, scale / 300));
  const palette = [colors.accent || '#e7c86a', colors.accent2 || colors.accent || '#8ab4ff', colors.liner || '#ffd1e8', colors.ink || '#333'];
  const items = Array.from({ length: preset.count }, (_, i) => {
    const t = (i * 137.508) % 100;               // golden-angle spread → even, non-random
    const size = preset.size[0] + ((i * 53) % (preset.size[1] - preset.size[0]));
    const dur = preset.dur[0] + ((i * 29) % (preset.dur[1] - preset.dur[0]));
    const delay = -((i * 41) % 12);
    const drift = ((i % 5) - 2) * 10;
    const spin = 120 + ((i * 67) % 260) * (i % 2 ? 1 : -1);
    const glyph = preset.glyphs[i % preset.glyphs.length];
    const op = 0.55 + ((i * 17) % 40) / 100;
    const cls = preset.mode === 'bokeh' ? 'inv-particle inv-particle--bokeh' : preset.mode === 'rise' ? 'inv-particle inv-particle--rise' : 'inv-particle';
    return { i, t, size: size * k, dur, delay, drift: drift * k, spin, glyph, op, color: preset.colored ? palette[i % palette.length] : undefined, cls };
  });
  return (
    <div className="inv-particles" aria-hidden data-testid="invite-particles">
      {items.map((p) => (
        <span key={p.i} className={p.cls}
          style={{ left: `${p.t}%`, fontSize: p.size, color: p.color,
            '--p-op': p.op, '--p-drift': `${p.drift}px`, '--p-spin': `${p.spin}deg`,
            animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s` }}>
          {p.glyph}
        </span>
      ))}
    </div>
  );
}

/**
 * Host-placed stickers — emoji/graphic accents dragged onto the card. Positions
 * are stored as fractions (0..1) of the card so they scale to any preview size.
 * Renders statically everywhere; in the builder (editable) each sticker can be
 * dragged, and the selected one is outlined.
 */
function StickerLayer({ stickers, scale, editable, selectedId, onSelect, onPatch }) {
  const onDown = (e, s) => {
    if (!editable || !onPatch) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect && onSelect(s.id);
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const move = (ev) => {
      const x = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height));
      onPatch(s.id, { x, y });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <>
      {stickers.map((s) => (
        <span
          key={s.id}
          onPointerDown={(e) => onDown(e, s)}
          onClick={editable ? (e) => { e.stopPropagation(); onSelect && onSelect(s.id); } : undefined}
          className={`absolute select-none leading-none ${editable ? 'cursor-move' : 'pointer-events-none'} ${editable && selectedId === s.id ? 'outline outline-2 outline-primary rounded-sm' : ''}`}
          style={{
            left: `${(s.x ?? 0.5) * 100}%`, top: `${(s.y ?? 0.3) * 100}%`,
            fontSize: (s.size || 40) * scale / 300,
            transform: `translate(-50%, -50%) rotate(${s.rot || 0}deg)`,
            zIndex: 4, color: s.color || undefined,
            filter: s.color ? undefined : 'saturate(1.02)',
            opacity: s.opacity ?? 1,
          }}
          aria-hidden
        >
          {s.glyph}
        </span>
      ))}
    </>
  );
}

/**
 * A scannable QR code (SVG, theme-able) built with the well-tested qrcode.react
 * encoder. `value` is the URL guests scan to open the invite and RSVP. Level "M"
 * error-correction + a quiet-zone margin keeps it readable when printed small.
 */
export function QrCode({ value, size = 120, fg = '#101B2D', bg = '#ffffff', className }) {
  if (!value) return null;
  return (
    <div className={className} data-testid="invite-qr">
      <QRCodeSVG value={value} size={size} fgColor={fg} bgColor={bg} level="M" marginSize={2} />
    </div>
  );
}

// Default icon for a details-card row when the host hasn't picked one.
const DETAIL_ICON = { address: '📍', location: '📍', map: '📍', parking: '🅿️', dress: '👗', attire: '👗', time: '🕒', schedule: '🕒', gift: '🎁', registry: '🎁', note: '📝', notes: '📝', food: '🍽️', menu: '🍽️', contact: '📞' };
function detailIcon(item) {
  if (item.icon) return item.icon;
  const key = (item.label || '').toLowerCase().split(/\s|\//)[0];
  return DETAIL_ICON[key] || '•';
}

/**
 * The "details" / second card — a companion surface holding logistics the main
 * invite is too pretty to carry: address, parking, dress code, schedule, notes,
 * plus (optionally) a scan-to-RSVP QR. Styled to match the invite's palette so
 * it reads as a matching insert card. Rendered on the public page and previews.
 */
export function DetailsFace({ design = {}, event = {}, scale = 300, rsvpUrl }) {
  const dc = design.detailsCard || {};
  const colors = design.colors || {};
  const font = design.font || { heading: "'Outfit', sans-serif", body: "'Manrope', sans-serif" };
  const items = (dc.items || []).filter((i) => (i.value || '').trim() || (i.label || '').trim());
  const showQr = design.showQr && rsvpUrl;
  const k = Math.min(1.2, Math.max(0.5, scale / 300));
  const pad = Math.round(scale * 0.085);
  return (
    <div className="relative overflow-hidden rounded-lg shadow-lift" data-testid="invite-details-face"
      style={{ width: scale, aspectRatio: '3 / 4', background: colors.bg || '#fff', color: colors.ink || '#101B2D',
        border: `1px solid ${colors.liner || 'rgba(0,0,0,0.08)'}`, padding: pad, fontFamily: font.body }}>
      <div className="h-full flex flex-col">
        <div className="text-center" style={{ marginBottom: 10 * k }}>
          <div style={{ fontFamily: font.heading, fontWeight: 700, fontSize: 20 * k, color: colors.ink, letterSpacing: -0.3 }}>
            {dc.title || 'The Details'}
          </div>
          {(event.title || dc.subtitle) && (
            <div style={{ fontSize: 11 * k, color: colors.accent || colors.ink, marginTop: 2 }}>{dc.subtitle || event.title}</div>
          )}
        </div>
        <div className="flex-1 overflow-hidden" style={{ display: 'flex', flexDirection: 'column', gap: 9 * k }}>
          {items.length === 0 && !showQr && (
            <div className="text-center" style={{ fontSize: 12 * k, opacity: 0.6, marginTop: 20 }}>
              Add address, parking, dress code &amp; more →
            </div>
          )}
          {items.map((it, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 * k }}>
              <span style={{ fontSize: 15 * k, lineHeight: 1, marginTop: 1 }}>{detailIcon(it)}</span>
              <div style={{ minWidth: 0 }}>
                {it.label && <div style={{ fontSize: 10 * k, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: colors.accent || colors.ink, opacity: 0.85 }}>{it.label}</div>}
                <div style={{ fontSize: 12.5 * k, lineHeight: 1.3, whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{it.value}</div>
              </div>
            </div>
          ))}
        </div>
        {showQr && (
          <div className="flex flex-col items-center" style={{ marginTop: 8 * k, gap: 4 * k }}>
            <div style={{ padding: 6 * k, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              {/* Always dark-on-white — a low-contrast QR (e.g. light ink on a dark card) won't scan. */}
              <QrCode value={rsvpUrl} size={Math.round(scale * 0.34)} fg="#101B2D" bg="#ffffff" />
            </div>
            <div style={{ fontSize: 10 * k, fontWeight: 600, color: colors.ink, opacity: 0.75 }}>Scan to RSVP</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function CardFace({ design, scale = 300, format = 'card', rich = false, playing = false, editable = false, selectedBlockId, onSelectBlock, selectedStickerId, onSelectSticker, onStickerPatch }) {
  if (CREATIVE_FORMATS.includes(format)) return <CreativeFace design={design} scale={scale} format={format} rich={rich} playing={playing} />;
  const colors = design.colors || {};
  const font = design.font || { heading: "'Outfit', sans-serif", body: "'Manrope', sans-serif" };
  // Everything scales with the card so text always fits the frame at any size.
  const k = Math.min(1.2, Math.max(0.4, scale / 300));
  // Generous side margins (Paperless-Post-style). Illustrated scenes set a
  // larger textInset so copy sits inside the artwork's open centre rather than
  // colliding with a wreath, frame or border.
  const padX = Math.round(scale * (design.textInset || 0.09));
  const padY = Math.round(scale * 0.085);
  const isFlyer = format === 'flyer';
  // Card = formal, delicate inner frame. Flyer = casual, bold poster edge.
  const outerBorder = isFlyer
    ? `${Math.max(3, Math.round(scale * 0.028))}px solid ${colors.accent || '#888'}`
    : `1px solid ${colors.liner || 'rgba(0,0,0,0.08)'}`;
  const hasMedia = design.video || design.photo || (design.photos && design.photos.length);
  const isShimmer = rich && design.animation === 'shimmer';
  const decor = design.decor || {};
  const layout = design.layout || 'centered';
  const justify = layout === 'top' || layout === 'editorial' ? 'flex-start' : layout === 'bottom' ? 'flex-end' : 'center';
  return (
    <div
      className={`relative overflow-hidden shadow-lift ${isFlyer ? 'rounded-sm' : 'rounded-lg'} ${isShimmer ? 'inv-shimmer' : ''} ${design.openFx === 'foil-sweep' ? `inv-fx-foilsweep inv-fx-foilsweep--${isDarkCard(colors.bg) ? 'metal' : 'paper'}` : ''}`}
      style={{ width: scale, aspectRatio: '3 / 4', ...bgLayer(design, colors), border: outerBorder }}
      onClick={editable ? () => onSelectBlock?.(null) : undefined}
      data-testid="invite-card-face" data-format={format}
    >
      {design.ambient && design.ambient !== 'none' && <AmbientLayer colors={colors} tone={isDarkCard(colors.bg) ? 'dark' : 'light'} />}
      {decor.pattern && <DecorPattern kind={decor.pattern} color={decor.patternColor || colors.accent} color2={colors.accent2} scale={scale} />}
      {design.scene && (
        <Scene
          name={design.scene}
          color={design.sceneColors?.color || colors.accent2 || colors.accent}
          color2={design.sceneColors?.color2 || colors.accent}
          accent={design.sceneColors?.accent || colors.accent}
          scale={scale}
        />
      )}
      {design.occasionArt && (
        <OccasionMotif
          name={design.occasionArt}
          color={colors.ink}
          accent={colors.accent}
          accent2={colors.accent2}
          scale={scale}
        />
      )}
      {decor.texture === 'grain' && <Grain opacity={decor.grainOpacity || 0.06} />}
      {design.motif && design.motif !== 'none' && <Motif motif={design.motif} accent={colors.accent || '#888'} k={k} />}
      {design.particles && design.particles !== 'none' && <ParticleLayer kind={design.particles} colors={colors} scale={scale} />}
      {decor.frame && <DecorFrame kind={decor.frame} color={decor.frameColor || colors.accent} scale={scale} />}
      <div className="relative z-[3] flex flex-col h-full">
        <MediaHero design={design} rich={rich} heightPct={hasMedia ? '42%' : '0%'} />
        <div className="flex-1 flex flex-col overflow-hidden" style={{ padding: `${padY}px ${padX}px`, justifyContent: justify }}>
          {(design.blocks || []).map((b) => (
            <Block key={b.id} block={b} colors={colors} font={font} k={k}
              editable={editable} selected={editable && selectedBlockId === b.id} onSelect={onSelectBlock} />
          ))}
        </div>
      </div>
      {((design.stickers && design.stickers.length) || editable) && (
        <StickerLayer stickers={design.stickers || []} scale={scale} editable={editable}
          selectedId={selectedStickerId} onSelect={onSelectSticker} onPatch={onStickerPatch} />
      )}
      {rich && design.music && (
        <div className="absolute left-1/2 -translate-x-1/2 z-20" style={{ bottom: Math.round(scale * 0.04) }} onClick={(e) => e.stopPropagation()}>
          <SongPlayer music={design.music} accent={colors.accent || '#2563EB'} autoPlay={playing} />
        </div>
      )}
      {rich && playing && <RevealBurst kind={design.animation} accent={colors.accent} />}
      {rich && playing && design.openFx === 'foil-sweep' && <FoilSweep />}
    </div>
  );
}

/**
 * Foil shimmer sweep — a specular highlight travels once across the card, as if
 * light were catching gold foil, trailing warm/cool fringes and a few motes.
 * Purely additive: it overlays the card and needs none of the envelope layers.
 * Styles live in invite-fx.css (.inv-fx-foilsweep-*).
 */
function FoilSweep() {
  const motes = [
    { id: 'fs-mote-1', x: '17%', y: '31%', s: 3, d: '640ms' },
    { id: 'fs-mote-2', x: '34%', y: '63%', s: 2, d: '706ms' },
    { id: 'fs-mote-3', x: '52%', y: '24%', s: 3, d: '772ms' },
    { id: 'fs-mote-4', x: '68%', y: '71%', s: 2, d: '838ms' },
    { id: 'fs-mote-5', x: '81%', y: '43%', s: 3, d: '900ms' },
  ];
  return (
    <div className="inv-fx-foilsweep-fx" aria-hidden="true">
      <div className="inv-fx-foilsweep-glare inv-fx-foilsweep-glare--ink">
        <div className="inv-fx-foilsweep-band">
          <i className="inv-fx-foilsweep-core" />
          <i className="inv-fx-foilsweep-fringe inv-fx-foilsweep-fringe--cool" />
          <i className="inv-fx-foilsweep-fringe inv-fx-foilsweep-fringe--warm" />
        </div>
      </div>
      <div className="inv-fx-foilsweep-glare inv-fx-foilsweep-glare--gloss">
        <div className="inv-fx-foilsweep-band"><i className="inv-fx-foilsweep-core" /></div>
      </div>
      {motes.map((m) => (
        <span key={m.id} className="inv-fx-foilsweep-mote"
          style={{ left: m.x, top: m.y, width: m.s, height: m.s, animationDelay: m.d }} />
      ))}
      <span className="inv-fx-foilsweep-edge" />
    </div>
  );
}

// Open-envelope preview for the Envelope tool (shows color, liner & stamp).
export function EnvelopeView({ design, scale = 300 }) {
  const colors = design.colors || {};
  const env = design.envelope?.color || colors.envelope || '#e9d5e5';
  const liner = design.liner?.color || colors.liner || '#c8b6e2';
  const w = scale, h = Math.round(scale * 0.78);
  return (
    <div className="relative" style={{ width: w, height: h + 40 }} data-testid="envelope-view">
      {/* card peeking up behind */}
      <div className="absolute left-1/2 -translate-x-1/2" style={{ top: 0, width: w * 0.62, zIndex: 1 }}>
        <CardFace design={design} scale={w * 0.62} />
      </div>
      {/* envelope body */}
      <div className="absolute bottom-0 rounded-md shadow-lift overflow-hidden" style={{ width: w, height: h, background: env, zIndex: 2 }}>
        {/* open flap showing liner */}
        <div className="absolute inset-x-0 top-0" style={{ height: '62%', background: liner, clipPath: 'polygon(0 0, 100% 0, 50% 96%)' }} />
        {/* front pocket */}
        <div className="absolute inset-x-0 bottom-0" style={{ height: '58%', background: env, clipPath: 'polygon(0 30%, 50% 0, 100% 30%, 100% 100%, 0 100%)' }} />
        <div className="absolute top-2 right-2 w-8 h-10 rounded-sm bg-white/85 border border-black/10 flex items-center justify-center text-base">{design.stamp?.emoji || '💌'}</div>
      </div>
    </div>
  );
}

// How long the full open choreography runs before we hand off to the flat card.
// Generous enough that the longest particle overlay (petals) finishes and fades
// before the swap, so nothing pops out. Kept in sync with the index.css timings.
const OPEN_MS = 2200;

/**
 * Particle / flourish overlays some opening effects add on top of the stage.
 * Purely additive and deterministic (variation derived from the item index, no
 * Math.random). Styles live in index.css (.inv-confetti-bit, .inv-petal, ...).
 */
function OpenFxOverlay({ fx, accent, ink, liner }) {
  if (fx === 'confetti-burst') {
    const cols = [accent, ink, '#FFFFFF'];
    const bits = Array.from({ length: 15 }, (_, i) => {
      const a = (i / 15) * Math.PI * 2;
      const dist = 120 + (i % 4) * 26;
      return {
        tx: Math.round(Math.cos(a) * dist), ty: Math.round(Math.sin(a) * dist + 120),
        rot: `${(i % 2 ? 1 : -1) * (180 + i * 22)}deg`, d: `${i * 30}ms`, c: cols[i % 3],
      };
    });
    return (
      <div className="inv-fxo" aria-hidden>
        {bits.map((b, i) => (
          <span key={i} className="inv-confetti-bit"
            style={{ background: hexA(b.c, 0.9), '--tx': `${b.tx}px`, '--ty': `${b.ty}px`, '--rot': b.rot, '--d': b.d }} />
        ))}
      </div>
    );
  }
  if (fx === 'petal-fall') {
    const petals = Array.from({ length: 11 }, (_, i) => ({
      left: `${(i * 9.3 + 4) % 96}%`,
      sway: `${(i % 2 ? 1 : -1) * (18 + i * 4)}px`,
      rot: `${(i % 2 ? 1 : -1) * (150 + i * 16)}deg`,
      dur: `${1200 + (i % 5) * 60}ms`, d: `${i * 70}ms`,
      c: i % 3 === 0 ? accent : hexA(accent, 0.7),
    }));
    return (
      <div className="inv-fxo" aria-hidden>
        {petals.map((p, i) => (
          <span key={i} className="inv-petal"
            style={{ left: p.left, background: hexA(p.c, 0.65), '--sway': p.sway, '--rot': p.rot, '--dur': p.dur, '--d': p.d }} />
        ))}
      </div>
    );
  }
  if (fx === 'lights-flicker') {
    const bulbs = Array.from({ length: 7 }, (_, i) => ({
      left: `${8 + i * 13.5}%`, top: `${7 + (i % 2) * 5}%`, d: `${i * 110}ms`,
    }));
    return (
      <div className="inv-fxo" aria-hidden>
        {bulbs.map((b, i) => (
          <span key={i} className="inv-bulb"
            style={{ left: b.left, top: b.top, background: hexA(accent, 0.95),
              boxShadow: `0 0 10px 3px ${hexA(accent, 0.6)}`, '--d': b.d }} />
        ))}
      </div>
    );
  }
  if (fx === 'ink-bloom') {
    return (
      <div className="inv-fxo" aria-hidden>
        <div className="inv-bloom" style={{ background: `radial-gradient(circle, ${hexA(accent, 0.5)}, ${hexA(accent, 0)} 70%)` }} />
      </div>
    );
  }
  if (fx === 'curtain-reveal') {
    const drape = (side) => ({
      backgroundImage: `repeating-linear-gradient(90deg, ${hexA(ink, 0.0)} 0 6px, ${hexA('#000000', 0.12)} 6px 7px), linear-gradient(90deg, ${hexA(accent, 0.9)}, ${hexA(liner || accent, 0.75)})`,
    });
    return (
      <div className="inv-fxo" aria-hidden>
        <div className="inv-drape inv-drape-l" style={drape('l')} />
        <div className="inv-drape inv-drape-r" style={drape('r')} />
      </div>
    );
  }
  return null;
}

/**
 * The 3D opening stage: wax seal breaks, flap swings open on its top hinge, the
 * card lifts out of the pocket, the envelope drops away. Shown while sealed and
 * while opening (same DOM, so there's no layout jump), then InvitePreview swaps
 * to the plain card. `fx` adds an .inv-fx-<key> hook for per-design effects.
 */
function EnvelopeStage({ design, format, scale, opening, onOpen, fx }) {
  const colors = design.colors || {};
  const env = colors.envelope || '#eee';
  const liner = design.liner?.color || colors.liner || '#f4f4f4';
  const accent = colors.accent || '#2563EB';

  const cardW = scale;
  const cardH = Math.round(cardW * 4 / 3);
  const envW = Math.round(scale * 1.07);
  // The envelope covers the bottom 72% of the stage, which is exactly the band
  // the sealed (scaled-down, nudged-down) card occupies — so nothing shows through.
  const envH = Math.round(cardH * 0.72);
  const stageH = cardH;
  const sealR = Math.round(scale * 0.075);

  return (
    <button
      type="button"
      onClick={opening ? undefined : onOpen}
      className={`inv-stage block group ${opening ? 'inv-opening' : 'cursor-pointer'} ${fx ? `inv-fx-${fx}` : ''}`}
      style={{ width: envW, height: stageH }}
      aria-label="Open your invitation"
      data-testid="invite-envelope"
    >
      {/* back panel — the card sits in front of this */}
      <div className="inv-env-body" style={{ height: envH, zIndex: 1 }}>
        <div className="absolute inset-0 rounded-lg shadow-lift" style={{ background: env }} />
      </div>

      {/* the card itself, tucked into the pocket until it lifts */}
      <div className="inv-card-slot" style={{ width: cardW, bottom: 0 }}>
        <CardFace design={design} scale={cardW} format={format} rich playing={opening} />
      </div>

      {/* front of the envelope: pocket, flap and seal */}
      <div className="inv-env-body" style={{ height: envH, zIndex: 3 }}>
        {/* Solid front panel — the classic X seams are drawn, not cut, so the
            sealed card can never peek through a gap between flap and pocket. */}
        <div
          className="inv-pocket rounded-lg"
          style={{
            height: '100%', background: env,
            backgroundImage: 'linear-gradient(135deg, transparent 49.6%, rgba(0,0,0,0.06) 50%), linear-gradient(225deg, transparent 49.6%, rgba(0,0,0,0.06) 50%)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
          }}
        />
        <div
          className="inv-flap"
          style={{
            height: '52%', background: liner,
            clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
            filter: 'brightness(0.99)',
          }}
        />
        {fx === 'wax-seal' ? (
          <>
            {/* the seal as two halves that split apart and drop */}
            <div className="inv-seal-half inv-seal-half-l shadow-lift" aria-hidden
              style={{ left: '50%', top: `calc(52% - ${sealR}px)`, width: sealR, height: sealR * 2,
                marginLeft: -sealR, background: accent, borderRadius: `${sealR}px 0 0 ${sealR}px` }} />
            <div className="inv-seal-half inv-seal-half-r shadow-lift" aria-hidden
              style={{ left: '50%', top: `calc(52% - ${sealR}px)`, width: sealR, height: sealR * 2,
                background: hexA(accent, 0.92), borderRadius: `0 ${sealR}px ${sealR}px 0` }} />
          </>
        ) : (
          <div
            className="inv-seal flex items-center justify-center text-white shadow-lift"
            style={{
              left: '50%', top: `calc(52% - ${sealR}px)`,
              width: sealR * 2, height: sealR * 2, borderRadius: '50%',
              background: accent, transform: 'translate(-50%, 0)',
              fontSize: design.envelope?.initial ? Math.round(sealR * 1.05) : Math.round(sealR * 0.95),
              fontFamily: design.envelope?.initial ? (design.font?.heading || 'serif') : undefined,
              fontWeight: 600,
            }}
          >
            {design.envelope?.initial || '✦'}
          </div>
        )}
        {design.envelope?.recipient && !opening && (
          <div className="absolute inset-x-0 flex justify-center" style={{ top: `calc(52% + ${sealR * 1.6}px)`, zIndex: 6 }}>
            <span style={{ fontFamily: design.font?.script || "'Dancing Script', cursive", fontSize: Math.round(scale * 0.075), color: colors.ink || '#333', opacity: 0.9 }}>
              {design.envelope.recipient}
            </span>
          </div>
        )}
        {opening && <OpenFxOverlay fx={fx} accent={accent} ink={colors.ink || '#333'} liner={liner} />}
        <div className="absolute right-3 rounded-sm bg-white/80 border border-black/10 flex items-center justify-center shadow-sm"
          style={{ top: Math.round(envH * 0.06), width: Math.round(scale * 0.11), height: Math.round(scale * 0.135), fontSize: Math.round(scale * 0.058), zIndex: 6 }}>
          {design.stamp?.emoji || '💌'}
        </div>
        {!opening && (
          <span className="absolute inset-x-0 flex justify-center" style={{ bottom: Math.round(envH * 0.08), zIndex: 6 }}>
            <span className="text-[11px] font-semibold px-3 py-1 rounded-full bg-white/85 text-foreground group-hover:bg-white transition-colors">Tap to open</span>
          </span>
        )}
      </div>
    </button>
  );
}

export default function InvitePreview({ design, format = 'card', mode = 'static', scale = 300 }) {
  const [phase, setPhase] = useState(mode === 'interactive' ? 'sealed' : 'open');

  // Hand off from the opening choreography to the flat card once it finishes.
  useEffect(() => {
    if (phase !== 'opening') return undefined;
    const t = setTimeout(() => setPhase('open'), OPEN_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (!design) return null;

  if (format === 'flyer' || CREATIVE_FORMATS.includes(format)) {
    return <div style={{ width: scale }} data-testid="invite-flyer"><CardFace design={design} scale={scale} format={format} rich playing={mode === 'interactive'} /></div>;
  }

  if (phase === 'sealed' || phase === 'opening') {
    return (
      <EnvelopeStage
        design={design} format={format} scale={scale}
        opening={phase === 'opening'}
        onOpen={() => setPhase('opening')}
        fx={design.openFx}
      />
    );
  }

  return (
    <div className="flex flex-col items-center gap-3" data-testid="invite-card-open">
      <CardFace design={design} scale={scale} format={format} rich playing={mode === 'interactive'} />
      {mode === 'interactive' && (
        <button onClick={() => setPhase('sealed')} className="text-xs text-muted-foreground hover:text-foreground underline">Replay envelope</button>
      )}
    </div>
  );
}
